import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useShopNotif } from "@/context/ShopNotifContext";
import { formatDateLong, waLink, todayWITA, formatRp } from "@/lib/constants";
import { OmzetTrendChart, PetugasBarChart } from "@/components/OmzetCharts";
import { StatusBadge, ServiceBadge } from "@/components/Badges";
import {
  CalendarCheck,
  CalendarPlus,
  Stethoscope,
  Flower,
  Hourglass,
  CheckCircle,
  Clock,
  WhatsappLogo,
  Tray,
  Storefront,
  Receipt,
  CaretRight,
  Wallet,
  ChatCircleDots,
} from "@phosphor-icons/react";

const CARDS = [
  { key: "reservasi_hari_ini", label: "Reservasi Hari Ini", icon: CalendarCheck, color: "#7C4DE0", bg: "#EEE6FF" },
  { key: "reservasi_besok", label: "Reservasi Besok", icon: CalendarPlus, color: "#0891b2", bg: "#cffafe" },
  { key: "periksa_konsultasi", label: "Periksa/Konsultasi Bidan Neny", icon: Stethoscope, color: "#be185d", bg: "#fce7f3" },
  { key: "prenatal_yoga", label: "Prenatal Yoga", icon: Flower, color: "#7c3aed", bg: "#f3e8ff" },
  { key: "menunggu_konfirmasi", label: "Menunggu Konfirmasi", icon: Hourglass, color: "#b45309", bg: "#fef3c7" },
  { key: "sudah_dikonfirmasi", label: "Sudah Dikonfirmasi", icon: CheckCircle, color: "#047857", bg: "#d1fae5" },
];

export default function Dashboard() {
  const shopNotif = useShopNotif();
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [omzet, setOmzet] = useState(null);
  const [occ, setOcc] = useState(null);
  const [paySum, setPaySum] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [hcUnread, setHcUnread] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/schedule/today").then((r) => setSchedule(r.data));
    api.get(`/calendar?date=${todayWITA()}`).then((r) => setOcc(r.data)).catch(() => {});
    api.get("/payments/summary").then((r) => setPaySum(r.data)).catch(() => {});
    api.get("/inbox-counts").then((r) => setInbox(r.data)).catch(() => {});
    api.get("/homecare/rooms/unread-summary").then((r) => setHcUnread(r.data?.total || 0)).catch(() => {});
    if (user?.role === "super_admin") {
      const start = todayWITA().slice(0, 8) + "01";
      api.get("/omzet", { params: { start, end: todayWITA() } }).then((r) => setOmzet(r.data)).catch(() => {});
    }
  }, [user]);

  if (user && user.role === "petugas") return <Navigate to="/omzet" replace />;

  return (
    <div className="animate-fade-up space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-800">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">
          {stats ? formatDateLong(stats.today) : "Memuat..."} · WITA
        </p>
      </div>

      {user && (user.role === "admin" || user.role === "super_admin") && (
        <button data-testid="dashboard-roomchat-shortcut" onClick={() => navigate("/homecare-chat-admin")} className="flex w-full items-center gap-4 rounded-2xl border border-purple-100 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#6A38C7] hover:shadow-md">
          <span className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#EEE6FF] text-[#6A38C7]">
            <ChatCircleDots size={24} weight="duotone" />
            {hcUnread > 0 && <span data-testid="dashboard-roomchat-badge" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-extrabold text-white">{hcUnread > 99 ? "99+" : hcUnread}</span>}
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-heading text-base font-extrabold text-slate-800">Room Chat Homecare</p>
              {hcUnread > 0 && <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-bold text-white">{hcUnread} PESAN BARU</span>}
            </div>
            <p className="mt-0.5 text-xs text-slate-500">Buka ruang chat resmi klien &amp; petugas homecare</p>
          </div>
          <CaretRight size={18} weight="bold" className="text-[#6A38C7]" />
        </button>
      )}

      {user && (user.role === "admin" || user.role === "super_admin") && (
        <div data-testid="dashboard-shop-shortcuts" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <button data-testid="dashboard-toko-produk" onClick={() => navigate("/toko-produk")} className="flex items-center gap-4 rounded-2xl border border-rose-100/70 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#7C4DE0] hover:shadow-md">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EEE6FF] text-[#6A38C7]"><Storefront size={24} weight="duotone" /></span>
            <div className="flex-1"><p className="font-heading text-base font-extrabold text-slate-800">Toko Produk</p><p className="mt-0.5 text-xs text-slate-500">Kelola kategori, produk, varian & stok</p></div>
            <CaretRight size={18} weight="bold" className="text-[#7C4DE0]" />
          </button>
          <button data-testid="dashboard-pesanan-toko" onClick={() => navigate("/pesanan-toko")} className="flex items-center gap-4 rounded-2xl border border-rose-100/70 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#7C4DE0] hover:shadow-md">
            <span className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#FCE7F3] text-[#DB2777]"><Receipt size={24} weight="duotone" />{shopNotif.unread > 0 && <span data-testid="dashboard-pesanan-toko-badge" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-extrabold text-white">{shopNotif.unread > 99 ? "99+" : shopNotif.unread}</span>}</span>
            <div className="flex-1"><p className="font-heading text-base font-extrabold text-slate-800">Pesanan Toko</p><p className="mt-0.5 text-xs text-slate-500">Proses pembayaran, ongkir & status pemenuhan</p></div>
            <CaretRight size={18} weight="bold" className="text-[#DB2777]" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {CARDS.map(({ key, label, icon: Icon, color, bg }) => (
          <div
            key={key}
            data-testid={`stat-${key}`}
            className="rounded-2xl border border-rose-100/70 bg-white p-4 shadow-[0_2px_10px_-3px_rgba(200,124,136,0.1)] transition-transform hover:-translate-y-0.5 sm:p-5"
          >
            <span
              className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl"
              style={{ backgroundColor: bg, color }}
            >
              <Icon size={22} weight="duotone" />
            </span>
            <p className="font-heading text-3xl font-extrabold text-slate-800">
              {stats ? stats[key] : "–"}
            </p>
            <p className="mt-1 text-xs font-medium leading-tight text-slate-500">{label}</p>
          </div>
        ))}
      </div>

      {inbox && (
        <div data-testid="dashboard-inbox" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <button data-testid="dashboard-booking-masuk" onClick={() => navigate("/booking-masuk")} className="flex items-center gap-4 rounded-2xl border border-rose-100/70 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#7C4DE0] hover:shadow-md">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EEE6FF] text-[#6A38C7]"><Tray size={24} weight="duotone" /></span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-heading text-3xl font-extrabold text-slate-800">{inbox.booking_masuk}</p>
                {inbox.booking_masuk > 0 && <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-bold text-white">PERLU TINDAKAN</span>}
              </div>
              <p className="text-xs font-medium text-slate-500">Booking Masuk — reservasi baru yang belum diproses</p>
            </div>
          </button>
          <button data-testid="dashboard-payment-pending" onClick={() => navigate("/pembayaran")} className="flex items-center gap-4 rounded-2xl border border-rose-100/70 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-amber-400 hover:shadow-md">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><Wallet size={24} weight="duotone" /></span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-heading text-3xl font-extrabold text-slate-800">{inbox.payment_pending}</p>
                {inbox.payment_pending > 0 && <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">VERIFIKASI</span>}
              </div>
              <p className="text-xs font-medium text-slate-500">Pembayaran menunggu verifikasi bukti</p>
            </div>
          </button>
        </div>
      )}

      {paySum && (
        <div data-testid="dashboard-payments" className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {[
            ["Menunggu Verifikasi", paySum.pending_verifikasi, false],
            ["DP Masuk Hari Ini", paySum.dp_hari_ini, true],
            ["Pelunasan Hari Ini", paySum.pelunasan_hari_ini, true],
            ["Terverifikasi Hari Ini", paySum.terverifikasi_hari_ini, true],
            ["Sisa Piutang", paySum.sisa_piutang, true],
          ].map(([label, val, isRp]) => (
            <div key={label} className="rounded-2xl border border-rose-100/70 bg-white p-4 shadow-sm">
              <p className="font-heading text-lg font-extrabold text-slate-800">{isRp ? formatRp(val || 0) : (val || 0)}</p>
              <p className="mt-0.5 text-xs text-slate-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      {omzet && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <OmzetTrendChart trend={omzet.trend} />
          <PetugasBarChart perPetugas={omzet.per_petugas} />
        </div>
      )}

      {occ && occ.staff && occ.staff.length > 0 && (
        <div data-testid="dashboard-occupancy">
          <div className="mb-4 flex items-center gap-2">
            <CalendarCheck size={22} weight="duotone" className="text-[#7C4DE0]" />
            <h2 className="font-heading text-xl font-bold text-slate-800">Kepadatan Hari Ini</h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {occ.staff.map((s) => {
              const total = occ.slots.length || 1;
              const inactive = ["Batal", "No Show", "Dibatalkan Otomatis", "Dibatalkan", "Kedaluwarsa", "Selesai"];
              const booked = occ.reservations.filter((r) => r.petugas_nama === s.nama && !inactive.includes(r.status)).length;
              const blocked = occ.blocked.filter((b) => b.petugas_nama === s.nama).length;
              const available = Math.max(0, total - booked - blocked);
              const pct = Math.round((booked / total) * 100);
              return (
                <div key={s.id} data-testid={`occ-${s.nama}`} className="rounded-2xl border border-rose-100/70 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="font-heading font-bold text-slate-800">{s.nama}</p>
                    <span className="rounded-full bg-[#F7F0FF] px-2 py-0.5 text-xs font-semibold text-[#6A38C7]">{s.jenis}</span>
                  </div>
                  <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full ${pct < 34 ? "bg-emerald-400" : pct < 67 ? "bg-amber-400" : "bg-rose-500"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span className="font-semibold text-slate-700">{booked} terisi</span>
                    <span>{available} tersedia</span>
                    {blocked > 0 && <span className="text-slate-500">🔒 {blocked} diblokir</span>}
                    <span className="ml-auto font-bold text-[#6A38C7]">{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div className="mb-4 flex items-center gap-2">
          <Clock size={22} weight="duotone" className="text-[#7C4DE0]" />
          <h2 className="font-heading text-xl font-bold text-slate-800">Jadwal Hari Ini</h2>
        </div>

        {schedule.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-rose-200 bg-white/50 p-10 text-center">
            <p className="text-sm text-slate-500">Belum ada reservasi untuk hari ini.</p>
            <button
              onClick={() => navigate("/tambah")}
              className="mt-3 rounded-xl bg-[#EEE6FF] px-5 py-2 text-sm font-semibold text-[#6A38C7] hover:bg-[#E8D5D0]"
            >
              + Tambah Reservasi
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {schedule.map((r) => (
              <div
                key={r.id}
                data-testid={`today-card-${r.id}`}
                className="flex items-center gap-4 rounded-2xl border border-rose-100/70 bg-white p-4 shadow-sm"
              >
                <div className="flex flex-col items-center rounded-xl bg-[#F7F0FF] px-3 py-2 text-[#6A38C7]">
                  <span className="font-heading text-lg font-extrabold leading-none">{r.jam_kunjungan}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading font-bold text-slate-800">{r.nama_lengkap}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <ServiceBadge service={r.jenis_layanan} />
                    <StatusBadge status={r.status} />
                  </div>
                  {r.usia_kehamilan_minggu ? (
                    <p className="mt-1 text-xs text-slate-500">Usia kehamilan: {r.usia_kehamilan_minggu} mgg</p>
                  ) : null}
                </div>
                <a
                  href={waLink(r.no_whatsapp, `Halo ${r.nama_lengkap}, mengingatkan jadwal Anda hari ini pukul ${r.jam_kunjungan} di GBS Clinic.`)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#25D366] text-white transition-transform active:scale-90"
                >
                  <WhatsappLogo size={20} weight="fill" />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
